import { clear } from "idb-keyval";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { queryPersister } from "src/lib/queryPersister";

const RELOAD_KEY = "cache-error-reload-at";
// If we already wiped the cache and reloaded within this window and still
// crashed, the error is real (not a stale-build artifact) — stop reloading and
// render the fallback so we don't trap the user in a reload loop.
const RELOAD_DEBOUNCE_MS = 10_000;

export async function recoverFromCacheCrash(
  reload: () => void = () => window.location.reload()
): Promise<void> {
  // Stop the worker before clearing IndexedDB so a queued/background query write cannot put the
  // stale payload back after the clear transaction completes but before the page reloads.
  queryPersister.stop();
  await Promise.allSettled([clear(), window.caches?.delete("fraction-cache") ?? Promise.resolve()]);
  reload();
}

/** A crash this soon after a cache-wipe reload is real, not stale persisted data. */
export function crashedRightAfterCacheWipe(): boolean {
  return Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) ?? 0) < RELOAD_DEBOUNCE_MS;
}

/** Wipe the persisted cache and reload, unless that was just tried. Returns whether it reloads. */
export function wipeCacheAndReloadOnce(error: unknown, errorInfo?: ErrorInfo): boolean {
  if (crashedRightAfterCacheWipe()) {
    console.error("App crashed again right after a cache-wipe reload — not retrying.", error, errorInfo);
    return false;
  }
  console.error("App crashed — wiping cache and reloading.", error, errorInfo);
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  // Wipe the live per-query persister (idb-keyval default store: db "keyval-store",
  // store "keyval") plus the legacy Cache API entry, then reload.
  void recoverFromCacheCrash();
  return true;
}

export class CacheErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    wipeCacheAndReloadOnce(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}
